import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Mail, Calendar, Database, Lock, Cookie, Globe, UserCheck } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-[#0a0f1e]">
      {/* Header */}
      <header className="border-b border-[#1e3a5f30] bg-[#0a0f1e]/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 text-white hover:text-[#00d4aa] transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-semibold">Back to Home</span>
            </Link>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#00d4aa]" />
              <span className="text-white font-medium">Legal</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="bg-[#111827] border border-[#1e3a5f30] rounded-2xl p-8 md:p-12">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#00d4aa]/10 mb-6">
              <Shield className="w-8 h-8 text-[#00d4aa]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-[#94a3b8]">Your privacy is important to us. Learn how we collect, use, and protect your data.</p>
          </div>

          {/* Last Updated */}
          <div className="flex items-center gap-2 text-[#94a3b8] text-sm mb-8 justify-center">
            <Calendar className="w-4 h-4" />
            <span>Last Updated: March 2026</span>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {/* Data Collection */}
            <section className="bg-[#1a2235] rounded-xl p-6 border border-[#1e3a5f30]">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-sm font-bold">1</span>
                Data We Collect
              </h2>
              <p className="text-[#94a3b8] mb-4">We collect the following types of personal information:</p>
              <ul className="text-[#94a3b8] space-y-3">
                <li className="flex items-start gap-3">
                  <UserCheck className="w-5 h-5 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Name</strong> - To personalize your experience and communication</span>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Email Address</strong> - For account management and notifications</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 text-[#00d4aa] flex items-center justify-center flex-shrink-0 mt-0.5">📞</span>
                  <span><strong className="text-white">Phone Number</strong> - For lead management and optional communication</span>
                </li>
              </ul>
            </section>

            {/* Data Usage */}
            <section className="bg-[#1a2235] rounded-xl p-6 border border-[#1e3a5f30]">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-sm font-bold">2</span>
                How We Use Your Data
              </h2>
              <p className="text-[#94a3b8] leading-relaxed">
                Your data is used <strong className="text-white">exclusively for CRM automation purposes</strong>, including:
              </p>
              <ul className="text-[#94a3b8] mt-4 space-y-2 list-disc list-inside">
                <li>Lead capture and management</li>
                <li>Automated email and WhatsApp communications</li>
                <li>AI-powered voice calls and follow-ups</li>
                <li>Analytics and reporting for your business</li>
                <li>Service improvements and customer support</li>
              </ul>
            </section>

            {/* Data Sharing */}
            <section className="bg-[#1a2235] rounded-xl p-6 border border-[#1e3a5f30]">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-sm font-bold">3</span>
                Data Sharing
              </h2>
              <div className="bg-[#00d4aa]/10 border border-[#00d4aa]/20 rounded-lg p-4">
                <p className="text-[#00d4aa] font-semibold flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  We never sell your data to third parties
                </p>
              </div>
              <p className="text-[#94a3b8] mt-4 leading-relaxed">
                Your data is never sold, rented, or shared with third parties for marketing purposes. We only share data with trusted service providers who help us operate our platform, and they are bound by strict confidentiality agreements.
              </p>
            </section>

            {/* Data Storage */}
            <section className="bg-[#1a2235] rounded-xl p-6 border border-[#1e3a5f30]">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-sm font-bold">4</span>
                Data Storage & Security
              </h2>
              <ul className="text-[#94a3b8] space-y-3">
                <li className="flex items-start gap-3">
                  <Database className="w-5 h-5 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  <span>Your data is stored securely on <strong className="text-white">MongoDB Atlas</strong>, a leading cloud database provider with enterprise-grade security.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  <span>All data transmissions are encrypted using <strong className="text-white">SSL/TLS encryption</strong>.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  <span>We implement industry-standard security measures to protect against unauthorized access.</span>
                </li>
              </ul>
            </section>

            {/* Cookies */}
            <section className="bg-[#1a2235] rounded-xl p-6 border border-[#1e3a5f30]">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-sm font-bold">5</span>
                Cookie Policy
              </h2>
              <ul className="text-[#94a3b8] space-y-3">
                <li className="flex items-start gap-3">
                  <Cookie className="w-5 h-5 text-[#f59e0b] mt-0.5 flex-shrink-0" />
                  <span>We use <strong className="text-white">essential cookies</strong> to maintain your session and preferences.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Cookie className="w-5 h-5 text-[#f59e0b] mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Analytics cookies</strong> help us understand how you use our platform to improve our services.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Cookie className="w-5 h-5 text-[#f59e0b] mt-0.5 flex-shrink-0" />
                  <span>You can manage cookie preferences through your browser settings.</span>
                </li>
              </ul>
            </section>

            {/* GDPR */}
            <section className="bg-[#1a2235] rounded-xl p-6 border border-[#1e3a5f30]">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-sm font-bold">6</span>
                GDPR Compliance
              </h2>
              <ul className="text-[#94a3b8] space-y-3">
                <li className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-[#3b82f6] mt-0.5 flex-shrink-0" />
                  <span>For users in the European Union, we comply with the <strong className="text-white">General Data Protection Regulation (GDPR)</strong>.</span>
                </li>
                <li className="flex items-start gap-3">
                  <UserCheck className="w-5 h-5 text-[#3b82f6] mt-0.5 flex-shrink-0" />
                  <span>You have the right to <strong className="text-white">access, rectify, or delete</strong> your personal data at any time.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-[#3b82f6] mt-0.5 flex-shrink-0" />
                  <span>You may request a copy of your data or ask us to erase it by contacting us.</span>
                </li>
              </ul>
            </section>

            {/* Contact */}
            <section className="bg-gradient-to-r from-[#00d4aa]/10 to-[#00d4aa]/5 rounded-xl p-6 border border-[#00d4aa]/20">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#00d4aa]" />
                Contact Us
              </h2>
              <p className="text-[#94a3b8] leading-relaxed">
                If you have any questions about this Privacy Policy or wish to exercise your data rights, please contact us at:
              </p>
              <a 
                href="mailto:hello@aileadmantra.com" 
                className="inline-flex items-center gap-2 mt-3 text-[#00d4aa] hover:text-[#00b894] font-medium transition-colors"
              >
                <Mail className="w-4 h-4" />
                hello@aileadmantra.com
              </a>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#1e3a5f30] bg-[#0a0f1e] py-8 mt-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <p className="text-white font-semibold">© 2026 AILeadMantra</p>
              <p className="text-[#94a3b8] text-sm">B2B SaaS Platform</p>
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/" className="text-[#94a3b8] hover:text-white transition-colors">Home</Link>
              <span className="text-[#1e3a5f]">|</span>
              <a href="/#robots" className="text-[#94a3b8] hover:text-white transition-colors">Features</a>
              <span className="text-[#1e3a5f]">|</span>
              <a href="/#pricing" className="text-[#94a3b8] hover:text-white transition-colors">Pricing</a>
              <span className="text-[#1e3a5f]">|</span>
              <a href="mailto:hello@aileadmantra.com" className="text-[#94a3b8] hover:text-white transition-colors">Contact</a>
            </div>
            <div className="flex gap-4 text-sm">
              <Link to="/terms" className="text-[#94a3b8] hover:text-white transition-colors">Terms</Link>
              <span className="text-[#1e3a5f]">|</span>
              <Link to="/privacy" className="text-[#00d4aa] font-medium">Privacy</Link>
              <span className="text-[#1e3a5f]">|</span>
              <Link to="/refund" className="text-[#94a3b8] hover:text-white transition-colors">Refund Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
